import { DocContent } from '../docsData';

const doc: DocContent = {
  title: 'Ellie',
  content: `
## Overview

Ellie is Pliable's AI data analyst. She enables natural language conversations with your data, allowing stakeholders to be fully self-service on top of governed entities and metrics. This eliminates the need for analysts to serve ad-hoc requests.

To get started, click on **Ellie** in the sidebar.

## Key Features

- **Natural Language Queries**: Ask questions in plain English
- **Context-Aware Conversations**: Build on previous questions in a dialogue format
- **Governed Semantic Layer**: Ensures consistent metrics and definitions
- **Visualization Generation**: Automatically creates charts and tables
- **Artifacts**: Persistent, shareable snapshots of generated analyses

## How It Works

1. **Ask your question**: Type a natural language question in the chat
2. **Ellie understands**: Ellie interprets your request using Pliable's semantic layer
3. **Analysis generated**: The system creates the appropriate visualization
4. **Refine or expand**: Continue the conversation to modify or enhance the analysis

### Example Conversation

\`\`\`
You: "Show me last year's total revenue"
Ellie: Generates single-value display of total sales for last year

You: "Can you show that broken down by product category and sales channel? Make it a bar chart"
Ellie: Creates a bar chart with dimensions: Product Category and Sales Channel
\`\`\`

## Semantic Layer, Not Raw SQL

Most AI analyst chatbots work by generating raw SQL from your natural language question. This means the LLM is responsible for understanding your schema, writing correct joins, and applying the right metric definitions every time. Because LLMs are non-deterministic, the same question can produce different SQL on different runs — leading to inconsistent or incorrect results that are difficult to detect.

Ellie works differently. Rather than writing SQL directly, Ellie translates your question into a structured request against Pliable's semantic layer. The semantic layer is where your entities, metrics, relationships, and business logic are already defined and governed. Ellie selects the right entities, dimensions, metrics, and filters — and then Pliable's data engine generates the query deterministically from those definitions, exactly the same way it does for charts and dashboards.

This means:

- **Consistent results**: Whether you ask Ellie a question or build the same chart on a dashboard, the underlying data is generated by the same engine using the same metric definitions. There is no gap between what Ellie returns and what your dashboards show.
- **Governed by design**: The LLM never decides how to calculate a metric or how to join tables. Those decisions live in the semantic layer and are applied uniformly. Ellie's job is to understand *what* you're asking for, not *how* to compute it.
- **Resilient to LLM non-determinism**: Because the non-deterministic part (natural language understanding) is separated from the deterministic part (query generation), you get the flexibility of conversational AI without sacrificing data accuracy.

## Artifacts

When Ellie generates a visualization or analysis, it is saved as an **artifact**. Artifacts are static, point-in-time snapshots of the data and visualization at the moment they were created.

### What Are Artifacts?

When you want to preserve a specific chart, table, or analysis that Ellie has generated, you can ask her to create an artifact. Unlike live dashboards that update automatically, artifacts preserve exactly what was generated at that moment, giving you a reliable historical record.

### Using Artifacts as Reports

Artifacts are ideal for sharing findings with your team:

- **Point-in-time reporting**: Use artifacts as static reports that capture the state of your data at a specific moment. This is useful for monthly reviews, quarterly summaries, or any analysis where you need to reference a fixed snapshot.
- **Discussion**: Team members can discuss artifacts directly, adding context and interpretation alongside the data. This keeps the conversation tied to the specific analysis rather than scattered across other channels.
- **Audit trail**: Because artifacts are immutable snapshots, they provide a clear record of what was known and when, which is valuable for compliance and decision tracking.

### Working with Artifacts

- Ask Ellie to create an artifact when you want to preserve a visualization or analysis (e.g., "save this as an artifact" or "create an artifact from this chart")
- You can revisit past artifacts from your conversation history
- Share artifacts with team members who can view and discuss them
- Use artifacts to compare how metrics have changed over time by reviewing snapshots from different dates

## Benefits

- **Eliminate ad-hoc requests**: Stakeholders become self-sufficient
- **Focus on strategic work**: Analysts tackle larger problems
- **Cost reduction**: Companies may not need dedicated analysts
- **Faster insights**: Natural language exploration accelerates discovery

## Getting Started

1. Click on **Ellie** in the sidebar
2. Type your first question about your data
3. Review the generated visualization
4. Refine your question using natural language
5. Share artifacts with your team for discussion

## Advanced Features

- **Context retention**: Ellie remembers previous questions in your session
- **Multi-step analysis**: Build complex analyses through conversation
- **Governance controls**: All queries use approved metrics and definitions
- **Export capabilities**: Share results directly from the conversation
`,
};

export default doc;
